import { Suspense, useCallback, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { RefreshCw, WifiOff } from 'lucide-react';
import { cn } from '@/utils/cn';
import { useAuth } from '@/contexts/AuthContext';
import { useChat } from '@/contexts/ChatContext';
import { useSafeNotifications } from '@/contexts/NotificationContext';
import { useQueryClient } from '@tanstack/react-query';
import { requestKeys, useRequestSummary } from '@/hooks/useAppointmentRequests';
import { SubscriptionWarningBanner } from '@/components/subscription/SubscriptionWarningBanner';
import { ReadOnlyBanner } from '@/components/subscription/ReadOnlyBanner';
import { PlanUpgradeHost } from '@/components/subscription/PlanUpgradeHost';
import { CurrencyScopeBanner } from '@/components/currency/CurrencyScopeBanner';
import { VisitConfirmHost } from '@/components/reservations/VisitConfirmHost';
import { GracePeriodReminder } from '@/components/subscription/GracePeriodReminder';
import { Sidebar } from './Sidebar';
import { Topbar } from './Topbar';
import { PageCrumbsProvider } from './PageCrumbsContext';
import { MobileDrawer } from './MobileDrawer';
import { BottomNav } from './BottomNav';
import {
  useFullscreen,
  useOpenNavGroups,
  useSidebarCollapsed,
} from './use-shell-state';
import { shellCssVars } from './layout-contract';
import type { ClinicRole } from './nav-config';
import { useInventorySuggestions } from '@/hooks/useInventorySuggestions';
import {
  TRANSFER_COUNTS_KEY,
  useOpenTransferCount,
} from '@/hooks/useOpenTransferCount';
import { ImportProgressDock } from '@/components/imports/ImportProgressDock';
import { ContentLoader } from '@/components/LazyLoad';
import { useLanguage } from '@/contexts/LanguageContext';
import { realtimeService } from '@/services/realtime.service';
import { runRegisteredRefreshes } from '@/lib/refresh-registry';
import { preloadWhenQuiet } from '@/lib/page-loads';
import {
  AppointmentsCalendar,
  Chat,
  DoctorQueueEnhanced,
  Finance,
  MyHome,
  NotificationsPage,
  Patients,
  ReceptionistQueue,
} from '@/pages/lazy';
import { useRouteMotion } from './use-route-motion';
import { usePullToRefresh } from './use-pull-to-refresh';
import { useKeyboardOpen, useOnline } from '@/hooks/useDeviceState';
import { useTabBarSuppressed } from './tab-bar-store';

/**
 * The pages a tab or the home screen leads to. Their code is fetched once
 * the first page is up, so a tab opens at once rather than after a
 * download, which on a clinic's Wi-Fi was a visible pause on every first tap.
 */
const PRELOAD = [
  MyHome,
  Patients,
  AppointmentsCalendar,
  ReceptionistQueue,
  DoctorQueueEnhanced,
  Finance,
  NotificationsPage,
  Chat,
];

/** Shortest spin, so a fast refresh still reads as "done" rather than a flicker. */
const MIN_REFRESH_MS = 600;

/**
 * The application shell.
 *
 * ── The layout contract, enforced here ──────────────────────
 * ONE scroll container: the `<main>` below. The root is a fixed-height
 * flex row with `overflow-hidden`, so nothing else can scroll the
 * page. `<main>` also owns page padding and the content width cap —
 * pages render into that slot and set no outer spacing of their own.
 *
 * See layout-contract.ts for the tokens and the full rationale.
 *
 * ── Note on mounting ────────────────────────────────────────
 * Mounted ONCE around the routes by ShellHost, so it survives navigation:
 * the top bar, tab bar and sidebar stay put, and the one Suspense boundary
 * here keeps the current page on screen while the next one loads. Pages
 * still wrap themselves in DashboardLayout, which stands down inside the
 * host (and renders this shell itself anywhere else).
 */

export interface AppShellProps {
  children: ReactNode;
  /** Hands the page a flex column and removes main's padding/scroll. */
  fullHeight?: boolean;
}

export function AppShell({ children, fullHeight = false }: AppShellProps) {
  const { t } = useTranslation();
  const { isRTL } = useLanguage();
  const { user, permissions } = useAuth();
  const { unreadTotal } = useChat();
  // Silent at zero: the badge only appears when there is something to act on.
  const { count: suggestionCount } = useInventorySuggestions();
  const transferCount = useOpenTransferCount();
  const { setOnNewNotification } = useSafeNotifications();

  const { collapsed, toggle: toggleCollapsed } = useSidebarCollapsed();
  const groups = useOpenNavGroups();
  const { isFullscreen, toggle: toggleFullscreen } = useFullscreen();

  const [drawerOpen, setDrawerOpen] = useState(false);

  const mainRef = useRef<HTMLElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const pullRef = useRef<HTMLDivElement>(null);
  useRouteMotion(mainRef, contentRef, isRTL);

  const keyboardOpen = useKeyboardOpen();
  const online = useOnline();
  const tabBarSuppressed = useTabBarSuppressed();

  // With the tab bar away, nothing needs clearing under the content: pages
  // read --shell-bottom-nav-pad (app.css), which this flag zeroes.
  useEffect(() => {
    document.documentElement.toggleAttribute(
      'data-tabbar-hidden',
      tabBarSuppressed,
    );
    return () => document.documentElement.removeAttribute('data-tabbar-hidden');
  }, [tabBarSuppressed]);

  const role = user?.clinicRole as ClinicRole | undefined;

  /* ── Appointment-request counter ─────────────────────────
     The same query as the Appointment Requests page title and banner
     (`useRequestSummary`), scoped by the same branch switcher, so the
     three numbers cannot disagree. It used to be fetched here on its own,
     clinic-wide, and bumped by hand when a notification arrived — which
     drifted from the page the moment a request was decided elsewhere. */
  const { data: requestSummary } = useRequestSummary();
  const queryClient = useQueryClient();

  // After the page on screen has its code and data, never alongside them.
  useEffect(() => preloadWhenQuiet(PRELOAD, queryClient), [queryClient]);

  // Everything on screen, both kinds: the reconnect signal reaches screens
  // that fetch by hand, and the refetch below waits for React Query's.
  // The page's own Refresh button handlers (hidden on touch) run as well.
  const refreshAll = useCallback(async () => {
    realtimeService.refreshAll();
    await Promise.all([
      queryClient.refetchQueries({ type: 'active' }, { cancelRefetch: false }),
      runRegisteredRefreshes(),
      new Promise((resolve) => setTimeout(resolve, MIN_REFRESH_MS)),
    ]);
  }, [queryClient]);

  // Split-pane pages scroll inside their own panes, so a pull on them
  // cannot be told from a scroll back to the top of a pane.
  const refreshing = usePullToRefresh({
    scrollRef: mainRef,
    indicatorRef: pullRef,
    enabled: !fullHeight,
    onRefresh: refreshAll,
  });

  useEffect(() => {
    setOnNewNotification((notification) => {
      if (
        notification.type === 'appointment_request' ||
        notification.type === 'pending_appointment_approval' ||
        notification.type === 'appointment_request_expiring' ||
        notification.type === 'appointment_request_expired' ||
        notification.type === 'appointment_approved' ||
        notification.type === 'appointment_rejected'
      ) {
        void queryClient.invalidateQueries({ queryKey: requestKeys.all });
      }
      if (notification.type === 'stock_transfer') {
        void queryClient.invalidateQueries({ queryKey: TRANSFER_COUNTS_KEY });
      }
    });
    return () => setOnNewNotification(undefined);
  }, [setOnNewNotification, queryClient]);

  const navProps = {
    permissions,
    role,
    counts: {
      pending: requestSummary?.open ?? 0,
      chat: unreadTotal,
      inventorySuggestions: suggestionCount,
      stockTransfers: transferCount,
    },
    groups,
  };

  return (
    <PageCrumbsProvider>
      <div
        style={shellCssVars}
        className="flex h-screen-mobile overflow-hidden bg-surface-0"
      >
        {/* Keyboard users can jump the ~60-item rail. */}
        <a
          href="#main-content"
          className={cn(
            'sr-only focus:not-sr-only focus:fixed focus:start-4 focus:top-4 focus:z-(--z-overlay)',
            'focus:rounded-md focus:bg-surface-1 focus:px-4 focus:py-2',
            'focus:text-body focus:font-semibold focus:text-brand-text',
            'focus:shadow-lg focus:outline-none focus:ring-2 focus:ring-primary/60',
          )}
        >
          {t('nav.skipToContent', 'Skip to content')}
        </a>

        {/* Desktop rail. Width animates; content is a flex sibling, so
          there is no layout shift when it collapses. */}
        <aside
          aria-label={t('nav.sidebar', 'Sidebar')}
          className={cn(
            'hidden shrink-0 border-e border-border-default lg:block print:hidden',
            'transition-[width] duration-(--shell-motion-slide) ease-(--ease-out-custom)',
            collapsed
              ? 'w-(--shell-sidebar-collapsed-w)'
              : 'w-(--shell-sidebar-w)',
          )}
        >
          <Sidebar {...navProps} collapsed={collapsed} />
        </aside>

        <MobileDrawer
          {...navProps}
          isOpen={drawerOpen}
          onOpenChange={setDrawerOpen}
        />

        <div className="flex w-full min-w-0 flex-1 flex-col overflow-hidden">
          <Topbar
            user={user}
            collapsed={collapsed}
            onToggleCollapsed={toggleCollapsed}
            isFullscreen={isFullscreen}
            onToggleFullscreen={toggleFullscreen}
          />

          {!online && (
            <div
              role="status"
              className="flex shrink-0 items-center justify-center gap-2 bg-warning-50 px-4 py-1.5 text-small font-medium text-warning-800 print:hidden"
            >
              <WifiOff aria-hidden className="size-icon shrink-0" />
              <span className="min-w-0">{t('nav.offline')}</span>
            </div>
          )}

          <SubscriptionWarningBanner />
          <ReadOnlyBanner />
          <CurrencyScopeBanner />
          <VisitConfirmHost />
          <PlanUpgradeHost />

          {/* Clips the pull-to-refresh indicator to the content area, so it
              slides out from under the top bar rather than over it. */}
          <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden">
            <div
              ref={pullRef}
              aria-hidden
              className="pointer-events-none absolute inset-x-0 top-2 z-(--z-header) mx-auto flex size-10 items-center justify-center rounded-full border border-border-default bg-surface-1 opacity-0 shadow-md"
            >
              <RefreshCw
                className={cn(
                  'size-5 text-brand-text',
                  refreshing ? 'animate-spin' : 'ds-pull-turn',
                )}
              />
            </div>
            {refreshing && (
              <span role="status" className="sr-only">
                {t('nav.refreshing')}
              </span>
            )}

            {/* THE scroll container. The element tree is the same in both
                modes, so a page switching `fullHeight` on mount (it does so
                before paint) is restyled, never remounted. */}
            <main
              ref={mainRef}
              id="main-content"
              data-scrollable
              className={cn(
                fullHeight
                  ? 'flex min-h-0 flex-1 flex-col overflow-hidden'
                  : 'flex-1 overflow-y-auto overflow-x-hidden',
              )}
            >
              <div
                ref={contentRef}
                className={cn(
                  fullHeight
                    ? 'flex min-h-0 flex-1 flex-col'
                    : 'mx-auto w-full max-w-(--shell-content-max-w) px-4 py-4 md:px-6 md:py-6 lg:px-8',
                )}
                style={
                  fullHeight
                    ? undefined
                    : {
                        // Clears the mobile tab bar. Zero on desktop, where
                        // there isn't one — the old shell padded 76px on every
                        // viewport and left dead space under every page.
                        paddingBottom:
                          'calc(env(safe-area-inset-bottom, 0px) + var(--shell-bottom-nav-pad, 0px) + var(--page-fab-pad, 0px) + 1.5rem)',
                      }
                }
              >
                <Suspense fallback={<ContentLoader />}>{children}</Suspense>
              </div>
            </main>
          </div>
        </div>

        <BottomNav
          permissions={permissions}
          role={role}
          hidden={keyboardOpen || tabBarSuppressed}
          onOpenDrawer={() => setDrawerOpen(true)}
        />

        <GracePeriodReminder />
        <ImportProgressDock />
      </div>
    </PageCrumbsProvider>
  );
}
